import { CurrencyEnum } from "@/lib/enum/currency.enum";
import { DetailFormComponentsEnum } from "@/lib/enum/detail-form-components.enum";
import { VehicleContractTypeEnum } from "@/lib/enum/vehicle-contract-type.enum";
import { FormConfig } from "@/lib/formable";
import { requiredRule } from "@/lib/rules/requiredRule";
import { RulesEnum } from "@/lib/rules/rules.map";
import { isDateRangeActive } from "@/lib/utility/is-date-range-active";
import { MrfiktivTimestampGen } from "@/services/mrfiktiv/v1/data-contracts";
import { ICompany } from "./company.entity";
import { IReference, Reference } from "./reference.entity";
import { VehicleContractType } from "./types/vehicle-contract-type";

export class VehicleContractBase {
  id: string;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.title"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.contract.title",
      rules: [requiredRule()]
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  title: string;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.description"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.contract.description"
    }
  })
  description?: string;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.number"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      label: "objects.contract.number"
    }
  })
  number?: string | undefined;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.contractType"],
    type: DetailFormComponentsEnum.SELECT_FIELD,
    props: {
      label: "objects.contract.contractType",
      rules: [requiredRule()],
      items: Object.values(VehicleContractTypeEnum)
    },
    rules: [RulesEnum.REQUIRED_RULE]
  })
  contractType: VehicleContractTypeEnum;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.startDate"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "date",
      label: "objects.contract.startDate"
    }
  })
  startDate?: string | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.endDate"],
    type: DetailFormComponentsEnum.TEXT_FIELD,
    props: {
      type: "date",
      label: "objects.contract.endDate"
    }
  })
  endDate?: string | null;

  @FormConfig({
    category: "common.nouns.configuration",
    searchKeywords: ["common.nouns.configuration", "objects.contract.currency"],
    type: DetailFormComponentsEnum.SELECT_FIELD,
    props: {
      label: "objects.contract.currency",
      items: Object.values(CurrencyEnum)
    }
  })
  currency?: CurrencyEnum | undefined;

  companyId?: string | null;

  isCompanyEntityLoading = false;
  company?: ICompany | null = null;

  refs?: IReference[] | undefined;

  timestamp?: MrfiktivTimestampGen;

  constructor(contract?: Partial<VehicleContractType>) {
    this.id = contract?.id ?? "";
    this.title = contract?.title ?? "";
    this.description = contract?.description;
    this.number = contract?.number;
    this.contractType = (contract?.contractType as VehicleContractTypeEnum) ?? VehicleContractTypeEnum.LEASING;
    this.startDate = contract?.startDate;
    this.endDate = contract?.endDate;
    this.currency = contract?.currency as CurrencyEnum;
    this.companyId = contract?.companyId;
    this.refs = Reference.filterDuplicates((contract?.refs ?? []).map((ref) => new Reference(ref)));
    this.timestamp = contract?.timestamp;
  }

  /**
   * Determines if a contract is active based on current date
   */
  get isActive(): boolean {
    return isDateRangeActive(this.startDate, this.endDate);
  }

  /**
   * isActive at a specific date
   * @param date
   * @returns
   */
  activeContractAtDate(ref?: string | Date | null): boolean {
    if (!ref) {
      ref = new Date();
    }
    return isDateRangeActive(this.startDate, this.endDate, new Date(ref));
  }
}
